import { DateTime } from "luxon";
// Local
import { convertToReadableTimestampDate } from "../../assets/js/convertToReadableTimestamp";
import currencyFormatter from "../../currency";
import { MembershipStatus } from "../../models/membership";
import { convertFSTimestampToLuxonDT } from "../../utils/convertFSTimestampToLuxonDT";
import { MembershipConfig } from "../../utils/templateAndMembershipUtils";

/**
 * This component is used to display the next billing action for a membership.
 *
 * "billing action" means one of two things --
 * - an invoice generated during the membership term (dues collection)
 * - a renewal, which will also require an invoice. renewals are expected to occur
 *   when the membership end date has passed.
 */
export default function NextBillingAction({
  membershipState,
  currency,
}: {
  membershipState: MembershipConfig | null;
  currency: string;
}): JSX.Element | null {
  if (!membershipState) return null;
  if (membershipState.status !== MembershipStatus.Active) return null;
  if (!membershipState.nextInvoiceDate && !membershipState.membershipEndDate) {
    return null;
  }
  const nextInvoiceText = getNextInvoiceText(membershipState, currency);
  const renewalText = getRenewalText(membershipState, currency);

  if (!membershipState.membershipEndDate) {
    if (!nextInvoiceText) return null; // would only be null if membership's nextInvoiceDate is not set. impossible in the current setup.
    return (
      <p className="text-right text-sm italic text-orange-800">
        {nextInvoiceText}
      </p>
    );
  }
  if (!membershipState.nextInvoiceDate) {
    if (!renewalText) return null; // would only be null if membership's membershipEndDate is not set. impossible in the current setup.
    return (
      <p className="text-right text-sm italic text-orange-800">{renewalText}</p>
    );
  }
  // both nextInvoiceDate and membershipEndDate are set: we need to determine
  // which one to display.
  // display renewal text if -
  //   - nextInvoiceDate has passed
  //   - nextInvoiceDate is later than membershipEndDate (..or equal to)
  //   - nextInvoiceDate is less than 8 days before membershipEndDate, i.e.
  //     nextInvoiceDate = 07/06/2025, membershipEndDate = 07/12/2025
  //       ^ pollMembershipsToCollectDues will skip this membership
  const nextInvoice = convertFSTimestampToLuxonDT(
    membershipState.nextInvoiceDate,
  ).startOf("day");
  const endDate = convertFSTimestampToLuxonDT(
    membershipState.membershipEndDate,
  ).startOf("day");

  const today = DateTime.now().toUTC().startOf("day");
  if (nextInvoice.diff(today, "days").days < 0) {
    if (!renewalText) return null;
    return (
      <p className="text-right text-sm italic text-orange-800">{renewalText}</p>
    );
  }

  if (nextInvoice.diff(endDate, "days").days > -8) {
    if (!renewalText) return null;
    return (
      <p className="text-right text-sm italic text-orange-800">{renewalText}</p>
    );
  }
  if (!nextInvoiceText) return null;
  return (
    <p className="text-right text-sm italic text-orange-800">
      {nextInvoiceText}
    </p>
  );
}

function getNextInvoiceText(
  membershipState: MembershipConfig | null,
  currency: string,
): string | null {
  if (!membershipState) return null;
  if (!membershipState.nextInvoiceDate) return null;

  const formattedAmount = currencyFormatter(membershipState.price, currency);

  const nextDate = convertToReadableTimestampDate(
    membershipState.nextInvoiceDate,
  );
  if (membershipState.invoiceMethod !== "automatic") {
    return `The next invoice (${formattedAmount}) should be manually created on ${nextDate}.`;
  }

  return `The next invoice (${formattedAmount}) will be automatically generated on ${nextDate}. ${getBasicInvoiceStr(membershipState)}`;
}

function getRenewalText(
  membershipState: MembershipConfig | null,
  currency: string,
): string | null {
  if (!membershipState) return null;
  const endDate = membershipState.membershipEndDate;
  if (!endDate) return null;

  let formattedAmount: string;
  try {
    // if it'll renew to a different template than it currently uses (aka "has a
    // renewal template"), we need to use price instead of renewalPrice.
    formattedAmount = currencyFormatter(
      membershipState.isRenewalTemplate
        ? membershipState.price
        : membershipState.renewalPrice,
      currency,
    );
  } catch (e) {
    formattedAmount = "[unknown amount]";
  }

  const renewalLuxon = convertFSTimestampToLuxonDT(endDate); // this is in UTC
  const ms = endDate.toMillis();
  const renewalDate = DateTime.fromMillis(ms)
    .plus({ days: 1 })
    .toFormat("LL/dd/yy");

  const today = DateTime.now().toUTC().startOf("day"); // compare dates in UTC
  if (membershipState.invoiceMethod !== "automatic") {
    if (renewalLuxon.diff(today, "days").days < 0) {
      return `Membership was due for manual renewal on ${renewalDate}, but renewal was not completed. Additionally, an invoice for ${formattedAmount} should have been created.`;
    }
    return `Due for manual renewal on ${renewalDate}. An invoice for ${formattedAmount} should be created at that time.`;
  }

  if (membershipState.willAutoRenew) {
    let baseWillRenew = `Automatic renewal will occur on ${renewalDate}.`;
    if (membershipState.isRenewalTemplate) {
      baseWillRenew += ` The membership will be renewed to ${membershipState.templateTitle}.`;
    }
    baseWillRenew += ` An invoice for ${formattedAmount} will be generated at that time. ${getBasicInvoiceStr(membershipState)}`;
    return baseWillRenew;
  } else {
    if (renewalLuxon.diff(today, "days").days < 0) {
      return `Membership was due for manual renewal on ${renewalDate}, but renewal was not completed. Additionally, an invoice for ${formattedAmount} should have been created.`;
    } else {
      return `Due for manual renewal on ${renewalDate}. An invoice for ${formattedAmount} should be created at that time.`;
    }
  }
}

/**
 * if the invoice is paid automatically, we don't email the invoice. but we might
 * email a receipt.
 *
 * if the invoice is not paid automatically, we don't email a receipt. but we might
 * email the invoice. invoice status depends on whether the invoice is emailed or not.
 */
function getBasicInvoiceStr(membershipState: MembershipConfig): string {
  if (membershipState.automaticallyPayInvoice) {
    if (membershipState.automaticallySendReceipt) {
      return "The card on file will be charged and a receipt will be emailed.";
    }
    return "The card on file will be charged.";
  }

  let str = "Payment will not be collected automatically.";
  if (membershipState.automaticallySendInvoice) {
    str +=
      " The invoice will be emailed to the customer and will have a status of submitted.";
  } else {
    str += " The invoice will have a status of pending.";
  }
  return str;
}
